import React from 'react';

import type {
  RoleplayExpression,
  RoleplayPersona,
  RoleplayPortraitConfig,
} from 'services/api/roleplayClient';

export const ROLEPLAY_EXPRESSIONS: Array<{ value: RoleplayExpression; label: string }> = [
  { value: 'at_ease', label: 'At ease' },
  { value: 'guarded', label: 'Guarded' },
  { value: 'anxious', label: 'Anxious' },
  { value: 'sad', label: 'Sad' },
  { value: 'overwhelmed', label: 'Overwhelmed' },
  { value: 'ashamed', label: 'Ashamed' },
  { value: 'defensive', label: 'Defensive' },
  { value: 'angry', label: 'Angry' },
  { value: 'numb', label: 'Numb' },
  { value: 'vulnerable', label: 'Vulnerable' },
  { value: 'relieved', label: 'Relieved' },
  { value: 'hopeful', label: 'Hopeful' },
];

const BUILT_IN_ASSETS: Record<string, string> = {
  'maya-emotions-v2': '/assets/roleplay-personas/maya-emotions-v2.png',
  'jeff-emotions-v2': '/assets/roleplay-personas/jeff-emotions-v2.png',
};

const EXPRESSION_CELL: Record<RoleplayExpression, { column: number; row: number }> = {
  at_ease: { column: 0, row: 0 },
  guarded: { column: 1, row: 0 },
  anxious: { column: 2, row: 0 },
  sad: { column: 0, row: 1 },
  overwhelmed: { column: 1, row: 1 },
  ashamed: { column: 2, row: 1 },
  defensive: { column: 0, row: 2 },
  angry: { column: 1, row: 2 },
  numb: { column: 2, row: 2 },
  vulnerable: { column: 0, row: 3 },
  relieved: { column: 1, row: 3 },
  hopeful: { column: 2, row: 3 },
};

const isLibraryConfig = (
  config: RoleplayPersona['portrait_config'],
): config is RoleplayPortraitConfig => (
  config.version === 2
  && typeof config.asset_key === 'string'
  && config.asset_key.length > 0
);

const RoleplayPersonaPortraitComponent: React.FC<{
  persona: RoleplayPersona;
  expression: RoleplayExpression;
  className?: string;
  ariaHidden?: boolean;
  showTestId?: boolean;
}> = ({ persona, expression, className = '', ariaHidden = false, showTestId = true }) => {
  const expressionLabel = ROLEPLAY_EXPRESSIONS.find((item) => item.value === expression)?.label ?? expression;
  const label = `${persona.name}, ${expressionLabel.toLowerCase()}`;

  if (persona.portrait_kind === 'static_media' && persona.image_url) {
    return <img src={persona.image_url} alt={label} aria-hidden={ariaHidden || undefined} className={`object-cover ${className}`} data-testid={showTestId ? `roleplay-portrait-${persona.key}` : undefined} data-expression={expression} />;
  }

  const config = persona.portrait_config;
  const assetUrl = isLibraryConfig(config) ? BUILT_IN_ASSETS[config.asset_key] : undefined;
  if (persona.portrait_kind !== 'library_image' || !isLibraryConfig(config) || !assetUrl) {
    return (
      <div role="img" aria-label={label} aria-hidden={ariaHidden || undefined} className={`flex items-center justify-center bg-gradient-to-br from-foundation-orange-100 to-foundation-calm-blue-100 text-3xl text-foundation-base-darkbase-dark-500 ${className}`} data-testid={showTestId ? `roleplay-portrait-${persona.key}` : undefined} data-expression={expression} data-portrait-fallback="monogram">
        {persona.name.trim().charAt(0).toUpperCase() || '?'}
      </div>
    );
  }

  const { column, row } = EXPRESSION_CELL[expression];
  return (
    <div
      role="img"
      aria-label={label}
      aria-hidden={ariaHidden || undefined}
      className={`overflow-hidden ${className}`}
      data-testid={showTestId ? `roleplay-portrait-${persona.key}` : undefined}
      data-expression={expression}
      data-persona-style="dimensional-v2"
      data-expression-cell={`${column},${row}`}
    >
      <span
        aria-hidden="true"
        className="block h-full w-full bg-no-repeat"
        data-testid={showTestId ? `roleplay-portrait-artwork-${persona.key}` : undefined}
        style={{
          backgroundImage: `url(${assetUrl})`,
          backgroundPosition: `${column * 50}% ${(row / 3) * 100}%`,
          backgroundSize: '300% 400%',
        }}
      />
    </div>
  );
};

export const RoleplayPersonaPortrait = React.memo(RoleplayPersonaPortraitComponent);
RoleplayPersonaPortrait.displayName = 'RoleplayPersonaPortrait';
